/**
 * CompanyRegistrationDetailPage — full detail view for a single request.
 *
 * Mirrors the "متابعة مهام الوكلاء" layout with additions:
 *   1) الطلب        — request meta, source, notes.
 *   2) المُقدَّم لأجله — client details.
 *   3) المرفقات      — read-only list of files uploaded on submission.
 *   4) رسوم الجهات الحكومية — pick type, amount, upload receipt.
 *   5) تحصيل الرسوم الخاصة بنا —
 *        radio: commission | admin_fees (mutually exclusive; other is
 *        struck-through with ✕).
 *        Selecting one shows amount + upload-receipt input.
 *   6) رسوم الرخصة — separate box (amount + upload receipt).
 *   7) الوكيل       — mode: none | registered | unregistered.
 *   8) حركة الطلب   — chronological stage log with add + edit-date.
 *   9) الرخصة التجارية — final box that promotes into "ملف الشركات".
 *
 * (iter-238, Feb 2026) — This file is now a thin composition. Every card
 * lives in its own module under `./company-registration/`.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, ClipboardList, GitBranch } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { AgentPageShell } from './agent/AgentPageShell';
import { API, authHeader } from './company-registration/helpers';
import { RequestHeader } from './company-registration/RequestHeader';
import { ClientBox } from './company-registration/ClientBox';
import { AttachmentsBox } from './company-registration/AttachmentsBox';
import { GovFeesBox } from './company-registration/GovFeesBox';
import { AdminFeesBox } from './company-registration/AdminFeesBox';
import { LicenseFeesBox } from './company-registration/LicenseFeesBox';
import { AgentBox } from './company-registration/AgentBox';
import { PaymentsBox } from './company-registration/PaymentsBox';
import { StagesBox } from './company-registration/StagesBox';
import { MovementTab } from './company-registration/MovementTab';
import { TradeLicenseBox } from './company-registration/TradeLicenseBox';

export default function CompanyRegistrationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [reg, setReg] = useState(null);
  const [loading, setLoading] = useState(true);
  // iter-264d (Feb 2026) — Per-box saving tracker. Each box passes its own
  // key when calling patch(); only THAT box's SmartSaveButton flips into
  // "✓ تم الحفظ" mode. A shared boolean caused every SmartSaveButton on
  // the page to turn green whenever any one box was saved.
  const [savingKey, setSavingKey] = useState(null);
  // iter-230 (Feb 2026) — 3-way tabs (mirrors AssignedTaskDetailPage):
  //   • request  → all data cards (client, fees, agent, payments, stages)
  //   • movement → assignee transfer history + summary
  const [tab, setTab] = useState('request');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API + '/api/admin/company-registrations/' + id, { headers: authHeader() });
      setReg(res.data?.data || null);
    } catch (e) {
      toast.error(t('فشل التحميل', 'Failed to load'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const patch = async (partial, key = 'global') => {
    setSavingKey(key);
    try {
      const res = await axios.put(API + '/api/admin/company-registrations/' + id, partial, { headers: authHeader() });
      setReg(res.data?.data || reg);
      toast.success(t('تم الحفظ', 'Saved'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
      // NOTE: We intentionally do NOT re-throw. The SmartSaveButton keeps
      // its "حفظ" state on failure because the box's local `dirty` flag
      // remains true (parent state didn't change). Throwing would spawn
      // unhandled-promise warnings for fire-and-forget callers such as
      // GovFeesBox which invoke `onSave(...)` without awaiting.
    } finally {
      setSavingKey(null);
    }
  };
  // Helper factory — returns a save function bound to a specific box key,
  // so each box's SmartSaveButton reads its own saving state via
  // `saving={savingKey === key}`.
  const patchFor = (key) => (partial) => patch(partial, key);

  // iter-235 (Feb 2026) — Hard-delete a registration entirely. Confirms via
  // a strong modal (typed request_code) rendered inside RequestHeader.
  const deleteRequest = async () => {
    try {
      await axios.delete(API + '/api/admin/company-registrations/' + id, { headers: authHeader() });
      toast.success(t('تم حذف الطلب نهائياً', 'Request deleted permanently'));
      navigate('/manage/cloudgm-agent/company-registration/track');
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
      throw e;
    }
  };

  const breadcrumb = [
    { label_ar: 'الرئيسية', label_en: 'Home', path: '/dashboard' },
    { label_ar: 'Cloud GM Company Agent', label_en: 'Cloud GM Company Agent', path: '/manage/cloudgm-agent' },
    { label_ar: 'تسجيل الشركات', label_en: 'Company Registration', path: '/manage/cloudgm-agent/company-registration' },
    { label_ar: 'متابعة تسجيل الشركات', label_en: 'Track Registrations', path: '/manage/cloudgm-agent/company-registration/track' },
    { label_ar: reg?.request_code || 'تفاصيل', label_en: reg?.request_code || 'Details', path: null },
  ];

  if (loading) {
    return (
      <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate(-1)}>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
        </div>
      </AgentPageShell>
    );
  }

  if (!reg) {
    return (
      <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate(-1)}>
        <div className="text-center text-[#71717A] py-16">{t('غير موجود', 'Not found')}</div>
      </AgentPageShell>
    );
  }

  return (
    <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate('/manage/cloudgm-agent/company-registration/track')}>
      <div className="max-w-5xl mx-auto space-y-6">
        <RequestHeader
          reg={reg}
          t={t}
          onDelete={deleteRequest}
          onMetaEdited={(fresh) => { if (fresh) setReg(fresh); }}
        />

        {/* iter-230 — Switch Button: الطلب / حركة الطلب */}
        <div className="flex items-center gap-2 bg-[#050505] border border-white/10 rounded-xl p-1">
          <button
            type="button"
            onClick={() => setTab('request')}
            data-testid="reg-tab-request"
            className={`flex-1 h-11 rounded-lg text-xs md:text-sm font-bold flex items-center justify-center gap-2 transition ${
              tab === 'request' ? 'bg-[#d4af37] text-black' : 'text-white/60 hover:text-white'
            }`}
          >
            <ClipboardList className="w-4 h-4" />
            {t('الطلب', 'Request')}
          </button>
          <button
            type="button"
            onClick={() => setTab('movement')}
            data-testid="reg-tab-movement"
            className={`flex-1 h-11 rounded-lg text-xs md:text-sm font-bold flex items-center justify-center gap-2 transition ${
              tab === 'movement' ? 'bg-purple-500 text-white' : 'text-white/60 hover:text-white'
            }`}
          >
            <GitBranch className="w-4 h-4" />
            {t('حركة الطلب', 'Movement')}
            {((reg.stages || []).filter(s => s.assignee_id).length) > 0 && (
              <span className="text-[10px] px-1.5 rounded-full bg-white/20 text-white">
                {(reg.stages || []).filter(s => s.assignee_id).length}
              </span>
            )}
          </button>
        </div>

        {tab === 'request' ? (
          <>
            <ClientBox reg={reg} t={t} isAr={isAr} />
            <AttachmentsBox reg={reg} onSave={patchFor('attachments')} saving={savingKey === 'attachments'} t={t} />
            <LicenseFeesBox reg={reg} onSave={patchFor('license_fees')} t={t} saving={savingKey === 'license_fees'} />
            <GovFeesBox reg={reg} onSave={patchFor('gov_fees')} t={t} saving={savingKey === 'gov_fees'} />
            <AdminFeesBox reg={reg} onSave={patchFor('admin_fees')} t={t} saving={savingKey === 'admin_fees'} />
            <PaymentsBox reg={reg} onSave={patchFor('payments')} t={t} saving={savingKey === 'payments'} isAr={isAr} />
            {/* iter-231e — Agent card moved right above the stages timeline per user request. */}
            <AgentBox reg={reg} onSave={patchFor('agent')} t={t} saving={savingKey === 'agent'} />
            <StagesBox reg={reg} onSave={patchFor('stages')} t={t} saving={savingKey === 'stages'} isAr={isAr} />
            <TradeLicenseBox reg={reg} onSave={patchFor('trade_license')} t={t} saving={savingKey === 'trade_license'} isAr={isAr} />
          </>
        ) : (
          <MovementTab reg={reg} t={t} isAr={isAr} />
        )}

        <div className="flex items-center justify-between gap-3 pb-8">
          <Button onClick={() => navigate('/manage/cloudgm-agent/company-registration/track')} variant="outline" className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e] gap-2">
            <ArrowLeft className="h-4 w-4" />
            {t('رجوع', 'Back')}
          </Button>
        </div>
      </div>
    </AgentPageShell>
  );
}
